// @ts-check
import imageCompression from 'browser-image-compression';
import { AlertCircle, CheckCircle, FileIcon, FileText, Film, Image, Upload, X } from 'lucide-react';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import api from '../utils/apiClient';
import { uploadVideoResumable } from '../utils/multipartVideoUpload';

// Upload types whose videos go through the resumable multipart pipeline (500MB cap).
const RESUMABLE_VIDEO_TYPES = ['about-media', 'showcase-media'];

/** Icon for a file name, picked from its extension. */
export const getFileIcon = (fileName) => {
  const ext = fileName.split('.').pop().toLowerCase();

  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) {
    return <Image size={20} className="text-blue-500" />;
  } else if (['mp4', 'mov', 'avi', 'webm'].includes(ext)) {
    return <Film size={20} className="text-purple-500" />;
  } else if (['pdf', 'doc', 'docx', 'txt'].includes(ext)) {
    return <FileText size={20} className="text-red-500" />;
  } else {
    return <FileIcon size={20} className="text-muted-foreground" />;
  }
};

const FileUpload = ({
  onUploadComplete,
  uploadType = 'task-attachment',
  multiple = true,
  maxFiles = 10,
  accept = '*/*',
  existingFiles = [],
  onUploadingChange,
}) => {
  const inputId = useId();
  const [files, setFiles] = useState(existingFiles);
  const [uploading, setUploading] = useState(false);
  useEffect(() => {
    onUploadingChange?.(uploading);
  }, [uploading, onUploadingChange]);
  // The parent may unmount us in the same tick an upload finishes (before the effect above can
  // report false), so always report idle on unmount.
  const onUploadingChangeRef = useRef(onUploadingChange);
  onUploadingChangeRef.current = onUploadingChange;
  useEffect(() => () => onUploadingChangeRef.current?.(false), []);
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [error, setError] = useState('');
  const [compressionStatus, setCompressionStatus] = useState({});

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  // Cloudflare Images and Cloudinary both hard-cap uploads at 10MB per image (not a
  // free-tier restriction — Cloudflare's own limits doc confirms 10MB / 100MP even on
  // paid usage), so oversized originals still need to be brought under that ceiling.
  // Quality is held high and fixed: browser-image-compression reduces PIXEL DIMENSIONS
  // (not JPEG quality) to hit maxSizeMB once quality-based encoding alone isn't enough,
  // which preserves far more visible detail than crushing quality does for the same
  // file-size budget — a mildly downscaled sharp image reads better than a full-resolution
  // blocky one.
  const COMPRESSION_QUALITY = 0.92;

  /**
   * Compress image file if it exceeds 10MB. Targets just under the 10MB Cloudflare Images
   * ceiling by downscaling resolution at high fixed quality, not by lowering quality.
   */
  const compressImageIfNeeded = useCallback(async (file) => {
    const fileSizeMB = file.size / (1024 * 1024);
    const MAX_SIZE_MB = 10;

    // Check if file is an image
    const isImage = file.type.startsWith('image/');

    // If file is <= 10MB or not an image, return as-is
    if (fileSizeMB <= MAX_SIZE_MB || !isImage) {
      return file;
    }

    try {
      // Update compression status
      setCompressionStatus((prev) => ({
        ...prev,
        [file.name]: {
          status: 'compressing',
          originalSize: fileSizeMB,
          quality: COMPRESSION_QUALITY,
        },
      }));

      const options = {
        maxSizeMB: 9.5, // Target slightly under the 10MB provider ceiling for safety margin
        maxWidthOrHeight: 8000, // Generous ceiling (Cloudflare allows up to 12,000px/100MP) — only
        // downscales if the image is genuinely larger than this; avoids needlessly softening
        // typical high-res product/DSLR photography (commonly up to ~8000px on the long edge).
        useWebWorker: true,
        quality: COMPRESSION_QUALITY,
        fileType: file.type,
      };

      const compressedBlob = await imageCompression(file, options);
      const compressedFile = new File([compressedBlob], file.name, { type: compressedBlob.type });
      const compressedSizeMB = compressedFile.size / (1024 * 1024);

      // Update compression status
      setCompressionStatus((prev) => ({
        ...prev,
        [file.name]: {
          status: 'completed',
          originalSize: fileSizeMB,
          compressedSize: compressedSizeMB,
          quality: COMPRESSION_QUALITY,
          saved: fileSizeMB - compressedSizeMB,
        },
      }));

      return compressedFile;
    } catch (error) {
      console.error('Compression failed:', error);
      setCompressionStatus((prev) => ({
        ...prev,
        [file.name]: {
          status: 'failed',
          error: error.message,
        },
      }));
      return file; // Return original if compression fails
    }
  }, []);

  const uploadFile = useCallback(
    async (file, onProgress) => {
      if (uploadType === 'about-media' && file.type.startsWith('video/')) {
        const url = await uploadVideoResumable(file, 'about_us', onProgress);
        return { url, public_id: url, format: file.name.split('.').pop(), size: file.size, resource_type: 'video' };
      }
      if (uploadType === 'showcase-media' && file.type.startsWith('video/')) {
        const url = await uploadVideoResumable(file, 'showcase', onProgress);
        return { url, public_id: url, format: file.name.split('.').pop(), size: file.size, resource_type: 'video' };
      }

      const formData = new FormData();
      formData.append('file', file);
      if (uploadType !== 'task-attachment') {
        formData.append('upload_type', uploadType);
      }

      const endpointMap = {
        'product-image': '/api/upload/product-image',
        'collection-image': '/api/upload/collection-image',
        'dress-photo': '/api/upload/dress-photo',
        'post-media': '/api/upload/post-media',
        'vendor-document': '/api/upload/vendor-document',
        'hero-media': '/api/upload/hero-media',
        'about-media': '/api/upload/about-media',
        'showcase-media': '/api/upload/about-media', // admin-only image upload; videos take the multipart path above
        'task-attachment': '/api/upload/task-attachment',
        'whatsapp-media': '/api/upload/whatsapp-media',
      };
      const endpoint = endpointMap[uploadType] || '/api/upload/task-attachment';

      return await api.upload(endpoint, formData);
    },
    [uploadType],
  );

  const handleFiles = useCallback(
    async (selectedFiles) => {
      if (!selectedFiles || selectedFiles.length === 0) return;

      setError(''); // Clear previous errors
      setCompressionStatus({}); // Clear compression status
      const fileArray = Array.from(selectedFiles);

      if (fileArray.length + files.length > maxFiles) {
        setError(
          `Maximum ${maxFiles} files allowed. You're trying to upload ${fileArray.length} files but only ${maxFiles - files.length} slots remaining.`,
        );
        return;
      }

      setUploading(true);

      // Compress files if needed (for images > 10MB)
      const compressedFiles = [];
      for (const file of fileArray) {
        const compressedFile = await compressImageIfNeeded(file);
        compressedFiles.push(compressedFile);
      }

      // After compression, check file sizes: 10MB images, 25MB documents, 100MB videos
      // (500MB for About Us / Showcase video, which goes through the resumable pipeline)
      // (matches backend/utils/media_types.py MAX_FILE_SIZE_MB / MAX_VIDEO_SIZE_MB / MULTIPART_VIDEO_MAX_MB / MAX_DOCUMENT_SIZE_MB)
      const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB
      const MAX_VIDEO_SIZE = RESUMABLE_VIDEO_TYPES.includes(uploadType) ? 500 * 1024 * 1024 : 100 * 1024 * 1024;
      const MAX_DOCUMENT_SIZE = 25 * 1024 * 1024; // 25MB
      const limitFor = (file) => {
        if (file.type.startsWith('video/')) return MAX_VIDEO_SIZE;
        if (file.type.startsWith('image/')) return MAX_IMAGE_SIZE;
        return MAX_DOCUMENT_SIZE;
      };
      const oversizedFiles = compressedFiles.filter((file) => file.size > limitFor(file));

      if (oversizedFiles.length > 0) {
        const fileList = oversizedFiles
          .map((f) => {
            const isVideo = f.type.startsWith('video/');
            const isImage = f.type.startsWith('image/');
            const limit = isVideo
              ? RESUMABLE_VIDEO_TYPES.includes(uploadType)
                ? '500MB'
                : '100MB'
              : isImage
                ? '10MB'
                : '25MB';
            const message = isImage
              ? `${f.name} (${formatFileSize(f.size)}) - Compression couldn't reduce below 10MB`
              : `${f.name} (${formatFileSize(f.size)}) - Exceeds ${limit} limit`;
            return `• ${message}`;
          })
          .join('\n');

        setError(
          `The following files exceed the size limit:\n\n${fileList}\n\n${oversizedFiles.some((f) => f.type.startsWith('image/')) ? 'Tip: Try reducing image dimensions or converting to JPEG format.' : 'Tip: Try trimming or compressing the video before uploading.'}`,
        );
        setUploading(false);
        setCompressionStatus({});
        return;
      }

      // Upload compressed files
      const uploadPromises = compressedFiles.map(async (file) => {
        setUploadProgress((prev) => ({ ...prev, [file.name]: 0 }));

        try {
          const onProgress = (status) => {
            setUploadProgress((prev) => ({
              ...prev,
              [file.name]: status.phase === 'compressing' ? 'compressing' : status.percent,
            }));
          };
          const result = await uploadFile(file, onProgress);

          setUploadProgress((prev) => ({ ...prev, [file.name]: 100 }));

          return {
            name: file.name,
            url: result.url,
            public_id: result.public_id,
            format: result.format,
            size: result.size,
            resource_type: result.resource_type,
            uploaded: true,
            error: null,
          };
        } catch (error) {
          setUploadProgress((prev) => ({ ...prev, [file.name]: -1 }));
          return {
            name: file.name,
            uploaded: false,
            error: error.message,
          };
        }
      });

      const results = await Promise.all(uploadPromises);
      const successfulUploads = results.filter((r) => r.uploaded);

      setFiles((prev) => [...prev, ...successfulUploads]);
      setUploading(false);
      setUploadProgress({});
      setCompressionStatus({});

      if (onUploadComplete) {
        onUploadComplete(successfulUploads);
      }

      // Show errors if any
      const failedUploads = results.filter((r) => !r.uploaded);
      if (failedUploads.length > 0) {
        const errorMessages = failedUploads.map((f) => `• ${f.name}: ${f.error}`).join('\n');
        setError(`Upload failed for:\n\n${errorMessages}`);
      }
    },
    [files, maxFiles, onUploadComplete, compressImageIfNeeded, uploadFile, uploadType],
  );

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  const removeFile = (index) => {
    const newFiles = files.filter((_, i) => i !== index);
    setFiles(newFiles);
    if (onUploadComplete) {
      onUploadComplete(newFiles);
    }
  };

  return (
    <div className="w-full">
      {/* Error Message */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-950/50 border border-red-200 dark:border-red-900 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" size={20} />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-red-900 dark:text-red-300 mb-1">Upload Error</h4>
              <p className="text-sm text-red-700 dark:text-red-300 whitespace-pre-line">{error}</p>
            </div>
            <button onClick={() => setError('')} className="text-red-400 hover:text-red-600 dark:hover:text-red-400">
              <X size={18} />
            </button>
          </div>
        </div>
      )}

      {/* Upload Area */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          dragActive
            ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/50'
            : 'border-input bg-muted/50 hover:border-muted-foreground'
        } ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          type="file"
          id={inputId}
          className="hidden"
          multiple={multiple}
          accept={accept}
          onChange={handleFileInput}
          disabled={uploading}
        />

        <label htmlFor={inputId} className="cursor-pointer flex flex-col items-center justify-center">
          <Upload className={`mb-4 ${dragActive ? 'text-blue-500' : 'text-muted-foreground/70'}`} size={48} />
          <p className="text-lg font-medium text-foreground/80 mb-2">
            {dragActive ? 'Drop files here' : 'Drag & drop files here'}
          </p>
          <p className="text-sm text-muted-foreground mb-4">or click to browse</p>
          <p className="text-xs text-muted-foreground/70">
            Max {maxFiles} files — images up to 10MB, videos up to{' '}
            {RESUMABLE_VIDEO_TYPES.includes(uploadType) ? '500MB' : '100MB'}, documents up to 25MB
          </p>
          <p className="text-xs text-blue-600 dark:text-blue-400 mt-2 font-medium">
            Images over 10MB will be automatically compressed
          </p>
        </label>
      </div>

      {/* Compression Status */}
      {Object.keys(compressionStatus).length > 0 && (
        <div className="mt-4 space-y-2">
          <h4 className="text-sm font-medium text-foreground/80">Compressing Images...</h4>
          {Object.entries(compressionStatus).map(([fileName, status]) => (
            <div
              key={fileName}
              className="flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-950/50 border border-blue-200 dark:border-blue-900 rounded-lg"
            >
              <Image className="text-blue-600 dark:text-blue-400" size={20} />
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">{fileName}</p>
                {status.status === 'compressing' && (
                  <p className="text-xs text-blue-600 dark:text-blue-400">
                    Compressing {status.originalSize.toFixed(2)}MB → target &lt;10MB (quality:{' '}
                    {(status.quality * 100).toFixed(0)}%)
                  </p>
                )}
                {status.status === 'completed' && (
                  <p className="text-xs text-green-600 dark:text-green-400 font-medium">
                    ✓ Compressed: {status.originalSize.toFixed(2)}MB → {status.compressedSize.toFixed(2)}MB (saved{' '}
                    {status.saved.toFixed(2)}MB)
                  </p>
                )}
                {status.status === 'failed' && (
                  <p className="text-xs text-red-600 dark:text-red-400">✗ Compression failed: {status.error}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Progress */}
      {Object.keys(uploadProgress).length > 0 && (
        <div className="mt-4 space-y-2">
          {Object.entries(uploadProgress).map(([fileName, progress]) => (
            <div key={fileName} className="flex items-center gap-3 p-2 bg-muted rounded-lg">
              {getFileIcon(fileName)}
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground/80">{fileName}</p>
                {progress === 'compressing' && (
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                    Optimizing video for the best quality…
                  </p>
                )}
                {typeof progress === 'number' && progress >= 0 && progress < 100 && (
                  <div className="w-full bg-accent rounded-full h-2 mt-1">
                    <div className="bg-blue-600 h-2 rounded-full transition-all" style={{ width: `${progress}%` }} />
                  </div>
                )}
                {progress === 100 && (
                  <p className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1 mt-1">
                    <CheckCircle size={14} /> Uploaded
                  </p>
                )}
                {progress === -1 && (
                  <p className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1 mt-1">
                    <AlertCircle size={14} /> Failed
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Uploaded Files List */}
      {files.length > 0 && (
        <div className="mt-4 space-y-2">
          <h4 className="text-sm font-medium text-foreground/80">Uploaded Files ({files.length})</h4>
          {files.map((file, index) => (
            <div
              key={index}
              className="flex items-center justify-between p-3 bg-card border border-border rounded-lg hover:border-input"
            >
              <div className="flex items-center gap-3 flex-1">
                {getFileIcon(file.name)}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{file.name}</p>
                  <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
                </div>
                <a
                  href={file.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-sm font-medium"
                >
                  View
                </a>
              </div>
              <button
                type="button"
                onClick={() => removeFile(index)}
                className="ml-2 p-1 text-red-500 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-950/70 rounded-lg"
              >
                <X size={18} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FileUpload;
